import { Fragment, FC } from "react";
import {
    ChangeNames,
    ChangeTypes,
    Configuration,
    DocumentState,
} from "../clients/FirebaseClient/types";
import { selectChange } from "../clients/ConfigurationClient/tools";
import { DocumentUpload } from "../components/DocumentUpload";
import { TopAreaText } from "../components/core/TopAreaText";
import { Summary } from "./summary";
import { MultilineConnectionTeaser } from "../components/core/MultiLineConnectionTeaser";
import { useStyles } from "../useStyles";
import {
    ChangeRenderer,
    ContributionsSectionRenderer,
    MultiChangeRenderer,
} from "../components/ChangeRenderer";
import { InfoBox } from "../components/core/InfoBox";

export type NavgateFunctionType = (screenName: string, scrollToChangeName?: string) => void;

export interface RenderFunctionOptions {
    navigate: NavgateFunctionType;
    scrollToChangeName?: string;
}

interface RenderComponentProps {
    configuration: Configuration;
    options?: RenderFunctionOptions;
}
export interface Screen {
    id: string;
    name: string;
    topArea?: JSX.Element;
    changes: (keyof ChangeTypes)[];
    RenderComponent?: FC<RenderComponentProps>;
}

export const summaryScreenName = "Zusammenfassung";
const screens: Screen[] = [
    {
        id: "productTypes",
        name: "Anschluss",
        changes: [
            ChangeNames.PRODUCT_TYPES,
            ChangeNames.PRODUCT_CLASSES,
            ChangeNames.ADJUSTABLE_LOAD_TYPES,
        ],
    },
    {
        id: "building_modification",
        name: "Gebäudeangaben",
        topArea: (
            <TopAreaText text="Hier erfassen Sie allgemeine Informationen zu Ihrem Objekt an der Bauadresse." />
        ),
        changes: [ChangeNames.MODIFICATION_BUILDING_USAGES, ChangeNames.MODIFICATION_CELLAR],
    },
    {
        id: "building_removal",
        name: "Demontage",
        changes: [
            ChangeNames.REMOVAL_POWER_SUBJECT,
            ChangeNames.REMOVAL_RECONSTRUCTION,
            ChangeNames.REMOVAL_RECONSTRUCTION_PRODUCT_CLASSES,
            ChangeNames.REMOVAL_BUILDING_OCCUPATION_CONFIRMATION,
        ],
        RenderComponent: (props: RenderComponentProps) => {
            const classes = useStyles();
            return (
                <Fragment>
                    <h2 className={classes.h2} style={{ marginLeft: 20 }}>
                        Demontage
                    </h2>
                    <TopAreaText text="Möchten Sie einen bestehenden Netzanschluss demontieren lassen?" />
                    {props.configuration.error ? (
                        <div className={classes.error}>{props.configuration.error}</div>
                    ) : (
                        <div />
                    )}
                    <div style={{ paddingLeft: 20, paddingRight: 20 }}>
                        <MultiChangeRenderer
                            changes={[
                                ChangeNames.REMOVAL_POWER_SUBJECT,
                                ChangeNames.REMOVAL_RECONSTRUCTION,
                                ChangeNames.REMOVAL_RECONSTRUCTION_PRODUCT_CLASSES,
                                ChangeNames.REMOVAL_BUILDING_OCCUPATION_CONFIRMATION,
                            ]}
                            configuration={props.configuration}
                            scrollToChangeName={props.options?.scrollToChangeName}
                        />
                    </div>
                </Fragment>
            );
        },
    },
    {
        id: "building_new_connection",
        name: "Gebäudeangaben",
        topArea: (
            <TopAreaText text="Hier erfassen Sie allgemeine Informationen zu Ihrem Objekt an der Bauadresse." />
        ),
        changes: [
            ChangeNames.REMOVAL_RECONSTRUCTION_BUILDING_USAGES,
            ChangeNames.NEW_CONNECTION_BUILDING_TYPES,
            ChangeNames.NEW_CONNECTION_CELLAR,
            ChangeNames.NEW_CONNECTION_BUILDING_USAGES,
        ],
    },
    {
        id: "building_increase",
        name: "Gebäudeangaben",
        topArea: (
            <TopAreaText text="Hier erfassen Sie allgemeine Informationen zu Ihrem Objekt an der Bauadresse." />
        ),
        changes: [ChangeNames.INCREASE_BUILDING_USAGES],
    },
    {
        id: "charger_connection_situation",
        name: "Netzzugang",
        topArea: (
            <TopAreaText text="Erfassen Sie hier bitte die Angaben zur Ihrer Anschlusssituation Ihrer Verbrauchsgeräte." />
        ),
        changes: [
            ChangeNames.CHARGER_LOCATION,
            ChangeNames.CHARGER_PUBLIC_PARKING_USAGES,
            ChangeNames.CHARGER_PRIVATE_PARKING_USAGES,
            ChangeNames.CHARGER_SUPPLY_CONNECTION,
            ChangeNames.ADJUSTABLE_LOAD_BUILDING_USAGE,
        ],
    },
    {
        id: "charger_connection_tech",
        name: "Anschlusstechnik",
        topArea: (
            <TopAreaText text="Möchten Sie einen neuen/separaten Netzanschluss für Ihre Ladeeinrichtung beauftragen?" />
        ),
        changes: [
            ChangeNames.CHARGER_CONNECTION_EXTERIOR_TYPE,
            ChangeNames.CHARGER_CONNECTION_PILLAR_DELIVERED_BY_SYSTEM_OPERATOR,
            ChangeNames.CHARGER_ADDITIONAL_ELECTRIC_DEMAND,
            ChangeNames.CHARGER_PERSONAL_CONTRIBUTION_CAN_DO_UNDERGROUND_WORK,
        ],
        RenderComponent: (props: RenderComponentProps) => {
            const classes = useStyles();
            return (
                <Fragment>
                    <h2 className={classes.h2} style={{ marginLeft: 20 }}>
                        Anschlusstechnik
                    </h2>
                    {props.configuration.error ? (
                        <div className={classes.error}>{props.configuration.error}</div>
                    ) : (
                        <div />
                    )}
                    <p className={classes.p} style={{ marginLeft: 20 }}>
                        Möchten Sie einen neuen/separaten Netzanschluss für Ihre Ladeeinrichtung
                        beauftragen?
                    </p>
                    <h4
                        className={classes.h4}
                        style={{ marginLeft: 20, marginBottom: 24, marginTop: 24 }}
                    >
                        Außenanschluss
                    </h4>
                    {props.configuration.state.chargerSupplyConnection?.state.value.new ? (
                        <InfoBox title="Hinweis" style={{ marginLeft: 20 }}>
                            <p>
                                Der neue Netzanschluss für Ihre Ladeeinrichtung erfolgt über einen
                                Außenanschluss. Bei der Außenanschlusstechnik wird der Netzanschluss
                                außerhalb des Gebäudes auf Ihrem Grundstück errichtet.
                            </p>
                        </InfoBox>
                    ) : (
                        <Fragment />
                    )}
                    <div style={{ paddingLeft: 20, paddingRight: 20 }}>
                        <MultiChangeRenderer
                            changes={[
                                ChangeNames.CHARGER_CONNECTION_EXTERIOR_TYPE,
                                ChangeNames.CHARGER_CONNECTION_PILLAR_DELIVERED_BY_SYSTEM_OPERATOR,
                                ChangeNames.CHARGER_ADDITIONAL_ELECTRIC_DEMAND,
                            ]}
                            configuration={props.configuration}
                            scrollToChangeName={props.options?.scrollToChangeName}
                        />
                    </div>
                    <div style={{ paddingLeft: 20, paddingRight: 20 }}>
                        <ContributionsSectionRenderer
                            changes={[
                                ChangeNames.CHARGER_PERSONAL_CONTRIBUTION_CAN_DO_UNDERGROUND_WORK,
                            ]}
                            configuration={props.configuration}
                            scrollToChangeName={props.options?.scrollToChangeName}
                        />
                    </div>
                </Fragment>
            );
        },
    },
    {
        id: "power_evaluation",
        name: "Leistungsermittlung",
        changes: [
            ChangeNames.REMOVAL_RECONSTRUCTION_HOUSING_UNITS,
            ChangeNames.NEW_CONNECTION_HOUSING_UNITS,
            ChangeNames.MODIFICATION_HOUSING_UNITS,
            ChangeNames.NEW_CONNECTION_GAS_DEMAND,
            ChangeNames.NEW_CONNECTION_ELECTRIC_BUSINESS_DEMAND,
            ChangeNames.MODIFICATION_ELECTRIC_BUSINESS_DEMAND,
            ChangeNames.NEW_CONNECTION_ELECTRIC_BOILER,
            ChangeNames.NEW_CONNECTION_ELECTRIC_HEAT_PUMP_V2,
            ChangeNames.NEW_CONNECTION_ELECTRIC_HEATING_SYSTEM_V2,
            ChangeNames.NEW_CONNECTION_ELECTRIC_ROOM_COOLING_DEVICE,
            ChangeNames.NEW_CONNECTION_ELECTRIC_ELEVATOR,
            ChangeNames.NEW_CONNECTION_ELECTRIC_SHARED_ELECTRICITY,
            ChangeNames.NEW_CONNECTION_ELECTRIC_SAUNA,
            ChangeNames.NEW_CONNECTION_ELECTRIC_LOADER_V2,
            ChangeNames.NEW_CONNECTION_ELECTRIC_MISCELLANEOUS,
            ChangeNames.NEW_CONNECTION_ELECTRIC_INFEED,
        ],
        RenderComponent: (props: RenderComponentProps) => {
            const classes = useStyles();
            let additional_consumers = [
                ChangeNames.NEW_CONNECTION_ELECTRIC_BOILER,
                ChangeNames.NEW_CONNECTION_ELECTRIC_HEAT_PUMP_V2,
                ChangeNames.NEW_CONNECTION_ELECTRIC_HEATING_SYSTEM_V2,
                ChangeNames.NEW_CONNECTION_ELECTRIC_ROOM_COOLING_DEVICE,
                ChangeNames.NEW_CONNECTION_ELECTRIC_ELEVATOR,
                ChangeNames.NEW_CONNECTION_ELECTRIC_SHARED_ELECTRICITY,
                ChangeNames.NEW_CONNECTION_ELECTRIC_SAUNA,
                ChangeNames.NEW_CONNECTION_ELECTRIC_LOADER_V2,
                ChangeNames.NEW_CONNECTION_ELECTRIC_MISCELLANEOUS,
                ChangeNames.NEW_CONNECTION_ELECTRIC_INFEED,
            ];

            return (
                <Fragment>
                    <h2 className={classes.h2} style={{ marginLeft: 20 }}>
                        Leistungsermittlung
                    </h2>
                    <TopAreaText text="Hier wird der Leistungsbedarf Ihres Objektes ermittelt." />
                    <div style={{ paddingLeft: 20, paddingRight: 20 }}>
                        <MultiChangeRenderer
                            changes={[
                                ChangeNames.REMOVAL_RECONSTRUCTION_HOUSING_UNITS,
                                ChangeNames.NEW_CONNECTION_HOUSING_UNITS,
                                ChangeNames.MODIFICATION_HOUSING_UNITS,
                                ChangeNames.NEW_CONNECTION_GAS_DEMAND,
                                ChangeNames.NEW_CONNECTION_ELECTRIC_BUSINESS_DEMAND,
                                ChangeNames.MODIFICATION_ELECTRIC_BUSINESS_DEMAND,
                            ]}
                            configuration={props.configuration}
                            scrollToChangeName={props.options?.scrollToChangeName}
                        />
                        {additional_consumers.some(
                            (consumer) => props.configuration.state[consumer]?.relevant,
                        ) && (
                            <Fragment>
                                <h3
                                    className={classes.h3}
                                    style={{ marginTop: 28, marginBottom: 16 }}
                                >
                                    Zusätzlicher Leistungsbedarf
                                </h3>
                                <p className={classes.p} style={{ marginBottom: 32 }}>
                                    Sind folgende Verbraucher vorhanden oder geplant?
                                </p>
                            </Fragment>
                        )}
                        <MultiChangeRenderer
                            changes={additional_consumers}
                            configuration={props.configuration}
                            scrollToChangeName={props.options?.scrollToChangeName}
                        />
                    </div>
                </Fragment>
            );
        },
    },
    {
        id: "charger_power_evaluation",
        name: "Bestandsermittlung",
        topArea: (
            <Fragment>
                <TopAreaText text="Die folgenden Angaben sind notwendig, um Ihren aktuellen Leistungsbedarf am Netzanschluss zu ermitteln. Somit können wir prüfen, ob Kapazitäten für Ihre Ladeeinrichtung bestehen." />
                <TopAreaText text="Für die korrekte Berechnung der Leistungsreserven am bestehenden Netzanschluss benötigen wir genaue Leistungsangaben." />
            </Fragment>
        ),
        changes: [
            ChangeNames.ADJUSTABLE_LOAD_HOUSING_UNITS,
            ChangeNames.ADJUSTABLE_LOAD_ELECTRICAL_BUSINESS_DEMAND,
            ChangeNames.ADJUSTABLE_LOAD_ELECTRICAL_BOILER,
            ChangeNames.ADJUSTABLE_LOAD_ELECTRICAL_HEAT_PUMP,
            ChangeNames.ADJUSTABLE_LOAD_ELECTRICAL_ELEVATOR,
            ChangeNames.ADJUSTABLE_LOAD_ELECTRIC_SHARED_ELECTRICITY,
            ChangeNames.ADJUSTABLE_LOAD_ELECTRIC_SAUNA,
            ChangeNames.ADJUSTABLE_LOAD_ELECTRIC_LOADER,
            ChangeNames.ADJUSTABLE_LOAD_ELECTRIC_MISCELLANEOUS,
            ChangeNames.ADJUSTABLE_LOAD_HOUSE_CONNECTION_FUSE_CAPACITY,
        ],
    },
    {
        id: "increase_current_power_evaluation",
        name: "Bestandsermittlung",
        changes: [
            ChangeNames.INCREASE_CURRENT_GAS_DEMAND,
            ChangeNames.INCREASE_CURRENT_HOUSING_UNITS,
            ChangeNames.INCREASE_CURRENT_ELECTRIC_BUSINESS_DEMAND,
            ChangeNames.INCREASE_CURRENT_ELECTRIC_BOILER,
            ChangeNames.INCREASE_CURRENT_ELECTRIC_HEAT_PUMP,
            ChangeNames.INCREASE_CURRENT_ELECTRIC_ELEVATOR,
            ChangeNames.INCREASE_CURRENT_ELECTRIC_SHARED_ELECTRICITY,
            ChangeNames.INCREASE_CURRENT_ELECTRIC_SAUNA,
            ChangeNames.INCREASE_CURRENT_ELECTRIC_LOADER,
            ChangeNames.INCREASE_CURRENT_ELECTRIC_MISCELLANEOUS,
            ChangeNames.INCREASE_CURRENT_HOUSE_CONNECTION_FUSE_CAPACITY,
        ],
        RenderComponent: (props: RenderComponentProps) => {
            const classes = useStyles();
            const additionalConsumerChanges = [
                ChangeNames.INCREASE_CURRENT_ELECTRIC_BOILER,
                ChangeNames.INCREASE_CURRENT_ELECTRIC_HEAT_PUMP,
                ChangeNames.INCREASE_CURRENT_ELECTRIC_ELEVATOR,
                ChangeNames.INCREASE_CURRENT_ELECTRIC_SHARED_ELECTRICITY,
                ChangeNames.INCREASE_CURRENT_ELECTRIC_SAUNA,
                ChangeNames.INCREASE_CURRENT_ELECTRIC_LOADER,
                ChangeNames.INCREASE_CURRENT_ELECTRIC_MISCELLANEOUS,
                ChangeNames.INCREASE_CURRENT_HOUSE_CONNECTION_FUSE_CAPACITY,
            ];
            return (
                <Fragment>
                    <h2 className={classes.h2} style={{ marginLeft: 20 }}>
                        Bestandsermittlung
                    </h2>
                    <TopAreaText text="Die folgenden Angaben sind notwendig, um Ihren aktuellen Bestand an Leistung zu ermitteln. Somit können wir prüfen, ob Kapazitäten für Ihre zusätzliche Leistung bestehen." />
                    <TopAreaText text="Machen Sie hier möglichst präzise Angaben, auch wenn Ihnen die genaue Leistung nicht bekannt ist." />
                    {props.configuration.error ? (
                        <div className={classes.error}>{props.configuration.error}</div>
                    ) : (
                        <div />
                    )}
                    <div style={{ paddingLeft: 20, paddingRight: 20 }}>
                        <MultiChangeRenderer
                            changes={[
                                ChangeNames.INCREASE_CURRENT_GAS_DEMAND,
                                ChangeNames.INCREASE_CURRENT_HOUSING_UNITS,
                                ChangeNames.INCREASE_CURRENT_ELECTRIC_BUSINESS_DEMAND,
                            ]}
                            configuration={props.configuration}
                            scrollToChangeName={props.options?.scrollToChangeName}
                        />
                        {additionalConsumerChanges.some(
                            (additionalConsumerChange) =>
                                props.configuration.state[additionalConsumerChange]?.relevant,
                        ) ? (
                            <Fragment>
                                <h3 className={classes.h3}>Zusätzliche Verbraucher</h3>
                                <p className={classes.p}>
                                    Sind weitere zusätzliche Verbraucher vorhanden?
                                </p>
                            </Fragment>
                        ) : null}

                        <MultiChangeRenderer
                            changes={additionalConsumerChanges}
                            configuration={props.configuration}
                            scrollToChangeName={props.options?.scrollToChangeName}
                        />
                    </div>
                </Fragment>
            );
        },
    },
    {
        id: "increase_expected_power_evaluation",
        name: "Leistungsbedarf zukünftig",
        changes: [
            ChangeNames.INCREASE_EXPECTED_GAS_DEMAND,
            ChangeNames.INCREASE_EXPECTED_HOUSING_UNITS,
            ChangeNames.INCREASE_EXPECTED_ELECTRIC_BUSINESS_DEMAND,
            ChangeNames.INCREASE_EXPECTED_ELECTRIC_BOILER,
            ChangeNames.INCREASE_EXPECTED_ELECTRIC_HEAT_PUMP_V2,
            ChangeNames.INCREASE_EXPECTED_ELECTRIC_HEATING_SYSTEM,
            ChangeNames.INCREASE_EXPECTED_ELECTRIC_ROOM_COOLING_DEVICE,
            ChangeNames.INCREASE_EXPECTED_ELECTRIC_ELEVATOR,
            ChangeNames.INCREASE_EXPECTED_ELECTRIC_SHARED_ELECTRICITY,
            ChangeNames.INCREASE_EXPECTED_ELECTRIC_SAUNA,
            ChangeNames.INCREASE_EXPECTED_ELECTRIC_LOADER,
            ChangeNames.INCREASE_EXPECTED_ELECTRIC_LOADER_V2,
            ChangeNames.INCREASE_EXPECTED_ELECTRIC_MISCELLANEOUS,
            ChangeNames.INCREASE_ADDITIONAL_NOTES,
        ],
        RenderComponent: (props: RenderComponentProps) => {
            const classes = useStyles();
            const additionalConsumerChanges = [
                ChangeNames.INCREASE_EXPECTED_ELECTRIC_BOILER,
                ChangeNames.INCREASE_EXPECTED_ELECTRIC_HEAT_PUMP_V2,
                ChangeNames.INCREASE_EXPECTED_ELECTRIC_HEATING_SYSTEM,
                ChangeNames.INCREASE_EXPECTED_ELECTRIC_ROOM_COOLING_DEVICE,
                ChangeNames.INCREASE_EXPECTED_ELECTRIC_ELEVATOR,
                ChangeNames.INCREASE_EXPECTED_ELECTRIC_SHARED_ELECTRICITY,
                ChangeNames.INCREASE_EXPECTED_ELECTRIC_SAUNA,
                ChangeNames.INCREASE_EXPECTED_ELECTRIC_LOADER_V2,
                ChangeNames.INCREASE_EXPECTED_ELECTRIC_MISCELLANEOUS,
            ];
            return (
                <Fragment>
                    <h2 className={classes.h2} style={{ marginLeft: 20 }}>
                        Leistungsbedarf zukünftig
                    </h2>
                    <TopAreaText text="Geben Sie hier bitte an, wodurch eine Leistungserhöhung erforderlich ist. Bitte wählen Sie hier den Grund Ihrer Leistungserhöhung aus." />
                    {props.configuration.error ? (
                        <div className={classes.error}>{props.configuration.error}</div>
                    ) : (
                        <div />
                    )}
                    <div style={{ paddingLeft: 20, paddingRight: 20 }}>
                        <MultiChangeRenderer
                            changes={[
                                ChangeNames.INCREASE_EXPECTED_GAS_DEMAND,
                                ChangeNames.INCREASE_EXPECTED_HOUSING_UNITS,
                                ChangeNames.INCREASE_EXPECTED_ELECTRIC_BUSINESS_DEMAND,
                            ]}
                            configuration={props.configuration}
                            scrollToChangeName={props.options?.scrollToChangeName}
                        />
                        {additionalConsumerChanges.some(
                            (additionalConsumerChange) =>
                                props.configuration.state[additionalConsumerChange]?.relevant,
                        ) ? (
                            <Fragment>
                                <h3 className={classes.h3}>Zusätzliche Verbraucher</h3>
                            </Fragment>
                        ) : null}
                        <MultiChangeRenderer
                            changes={additionalConsumerChanges}
                            configuration={props.configuration}
                            scrollToChangeName={props.options?.scrollToChangeName}
                        />
                        <ChangeRenderer changeName={ChangeNames.INCREASE_ADDITIONAL_NOTES} />
                    </div>
                </Fragment>
            );
        },
    },
    {
        id: "charger_technical_details",
        name: "Technische Daten",
        changes: [
            ChangeNames.CHARGER_CHARGING_STATIONS,
            ChangeNames.CHARGER_LOAD_MANAGEMENT,
            ChangeNames.ADJUSTABLE_LOAD_ELECTRIC_INSTALLATION_AGREEMENT,
            ChangeNames.ADJUSTABLE_LOAD_DATA_SHEET_DOWNLOAD,
        ],
        RenderComponent: (props: RenderComponentProps) => {
            const classes = useStyles();
            const chargerChangeNames = [
                ChangeNames.CHARGER_CHARGING_STATIONS,
                ChangeNames.CHARGER_LOAD_MANAGEMENT,
            ];
            const isChargerRelevant = chargerChangeNames.some(
                (changeName) => props.configuration.state[changeName]?.relevant,
            );
            const otherChangesNames = [ChangeNames.ADJUSTABLE_LOAD_DATA_SHEET_DOWNLOAD];
            const isOtherRelevant = otherChangesNames.some(
                (changeName) => props.configuration.state[changeName]?.relevant,
            );

            return (
                <Fragment>
                    {isChargerRelevant ? (
                        <h2 className={classes.h2} style={{ marginLeft: 20 }}>
                            Technische Daten Ladeeinrichtung
                        </h2>
                    ) : null}
                    {props.configuration.error ? (
                        <div className={classes.error}>{props.configuration.error}</div>
                    ) : (
                        <div />
                    )}
                    <div style={{ paddingLeft: 20, paddingRight: 20 }}>
                        <MultiChangeRenderer
                            changes={chargerChangeNames}
                            configuration={props.configuration}
                            scrollToChangeName={props.options?.scrollToChangeName}
                        />
                    </div>
                    {isOtherRelevant ? (
                        <h2 className={classes.h2} style={{ marginLeft: 20 }}>
                            Technische Daten Wärmepumpe und Gerät zur Raumkühlung
                        </h2>
                    ) : null}
                    <div style={{ paddingLeft: 20, paddingRight: 20 }}>
                        <MultiChangeRenderer
                            changes={otherChangesNames}
                            configuration={props.configuration}
                            scrollToChangeName={props.options?.scrollToChangeName}
                        />
                    </div>
                    <h2 className={classes.h2} style={{ marginLeft: 20 }}>
                        Hinweis
                    </h2>
                    <div style={{ paddingLeft: 20, paddingRight: 20 }}>
                        <MultiChangeRenderer
                            changes={[ChangeNames.ADJUSTABLE_LOAD_ELECTRIC_INSTALLATION_AGREEMENT]}
                            configuration={props.configuration}
                            scrollToChangeName={props.options?.scrollToChangeName}
                        />
                    </div>
                </Fragment>
            );
        },
    },
    {
        id: "connection_technology",
        name: "Anschlusstechnik",
        topArea: (
            <Fragment>
                <TopAreaText text="Wählen Sie eine entsprechende Anschlusstechnik für Ihren Netzanschluss aus." />
                <TopAreaText
                    text="Die gewünschte Ausführungsvariante ist von der Art des örtlichen
                    Versorgungsnetzes der Syna GmbH abhängig (z. B. Freileitung- oder Kabelnetz)."
                />
            </Fragment>
        ),
        changes: [
            ChangeNames.NEW_CONNECTION_GAS_FEEDS,
            ChangeNames.NEW_CONNECTION_POWER_FEEDS,
            ChangeNames.NEW_CONNECTION_EXTERIOR_TYPES,
            ChangeNames.NEW_CONNECTION_CONNECTION_PILLAR_DELIVERED_BY_SYSTEM_OPERATOR,
            ChangeNames.NEW_CONNECTION_ROOF_TYPES,
            ChangeNames.NEW_CONNECTION_BUILDING_INTAKES,
        ],
    },
    {
        id: "connection_technology_modification",
        name: "Anschlusstechnik",
        topArea: (
            <Fragment>
                <TopAreaText text="Hier erfassen Sie die Änderung an Ihrem bestehenden Stromnetzanschluss." />
            </Fragment>
        ),
        changes: [ChangeNames.MODIFICATION_POWER_FEEDS],
    },
    {
        id: "building_modification_power_cable",
        name: "Änderung",
        changes: [
            ChangeNames.MODIFICATION_POWER_OVERHEAD,
            ChangeNames.MODIFICATION_POWER_OVERHEAD_ROOF_PILLAR_REASSEMBLY_LOCATION,
            ChangeNames.MODIFICATION_POWER_CABLE,
            ChangeNames.MODIFICATION_POWER_CABLE_RELOCATION_DISTANCE,
            ChangeNames.MODIFICATION_POWER_CABLE_JUNCTION_BOX_RELOCATION_DISTANCE,
            ChangeNames.MODIFICATION_POWER_JUNCTION_BOX_REPLACEMENT_REASON,
            ChangeNames.MODIFICATION_GAS_RELOCATION_DISTANCE,
            ChangeNames.MODIFICATION_REASON,
            ChangeNames.MODIFICATION_PERSONAL_CONTRIBUTION_CAN_DO_UNDERGROUND_WORK,
            ChangeNames.MODIFICATION_PERSONAL_CONTRIBUTION_CAN_DO_WALL_BUSHING,
        ],
        RenderComponent: (props: RenderComponentProps) => {
            const classes = useStyles();
            return (
                <Fragment>
                    <h2 className={classes.h2} style={{ marginLeft: 20 }}>
                        Änderung
                    </h2>
                    <TopAreaText text="Geben Sie hier bitte detaillierte Angaben zu Ihrem Änderungswunsch an." />
                    {props.configuration.error ? (
                        <div className={classes.error}>{props.configuration.error}</div>
                    ) : (
                        <div />
                    )}
                    <div style={{ paddingLeft: 20, paddingRight: 20 }}>
                        <MultiChangeRenderer
                            changes={[
                                ChangeNames.MODIFICATION_POWER_OVERHEAD,
                                ChangeNames.MODIFICATION_POWER_OVERHEAD_ROOF_PILLAR_REASSEMBLY_LOCATION,
                                ChangeNames.MODIFICATION_POWER_CABLE,
                                ChangeNames.MODIFICATION_POWER_CABLE_RELOCATION_DISTANCE,
                                ChangeNames.MODIFICATION_POWER_CABLE_JUNCTION_BOX_RELOCATION_DISTANCE,
                                ChangeNames.MODIFICATION_POWER_JUNCTION_BOX_REPLACEMENT_REASON,
                                ChangeNames.MODIFICATION_GAS_RELOCATION_DISTANCE,
                                ChangeNames.MODIFICATION_REASON,
                            ]}
                            configuration={props.configuration}
                            scrollToChangeName={props.options?.scrollToChangeName}
                        />
                        <ContributionsSectionRenderer
                            changes={[
                                ChangeNames.MODIFICATION_PERSONAL_CONTRIBUTION_CAN_DO_UNDERGROUND_WORK,
                                ChangeNames.MODIFICATION_PERSONAL_CONTRIBUTION_CAN_DO_WALL_BUSHING,
                            ]}
                            configuration={props.configuration}
                            scrollToChangeName={props.options?.scrollToChangeName}
                        />
                    </div>
                </Fragment>
            );
        },
    },
    {
        id: "multiline_connection",
        name: "Hauseinführung",
        changes: [
            ChangeNames.NEW_CONNECTION_MULTILINE_DESIRES,
            ChangeNames.NEW_CONNECTION_MULTILINE_BUILDING_INTAKE_SPECIFICATION,
            ChangeNames.NEW_CONNECTION_MULTILINE_CONTACT_ALLOWED,
            ChangeNames.NEW_CONNECTION_MULTILINE_VARIANTS,
            ChangeNames.NEW_CONNECTION_MULTILINE_CELLAR_OUTER_WALL,
            ChangeNames.NEW_CONNECTION_MULTILINE_CELLAR_OUTER_WALL_BUSHING,
            ChangeNames.NEW_CONNECTION_MULTILINE_CELLAR_CASING_PIPE_DELIVERED_BY_DISTRIBUTION_SYSTEM_OPERATOR,
            ChangeNames.NEW_CONNECTION_MULTILINE_BASE_PLATE_HEIGHT,
            ChangeNames.NEW_CONNECTION_MULTILINE_INSTALLATION_FRAME_INSTALLED_BY_DISTRIBUTION_SYSTEM_OPERATOR,
            ChangeNames.NEW_CONNECTION_MULTILINE_CELLAR_INSTALLATION_FRAME_INSTALLED_BY_DISTRIBUTION_SYSTEM_OPERATOR,
            ChangeNames.NEW_CONNECTION_MULTILINE_PERSONAL_CONTRIBUTION_CAN_DO_UNDERGROUND_WORK,
            ChangeNames.NEW_CONNECTION_MULTILINE_PERSONAL_CONTRIBUTION_MUST_DO_UNDERGROUND_WORK,
            ChangeNames.NEW_CONNECTION_PERSONAL_CONTRIBUTION_CAN_DO_UNDERGROUND_WORK,
            ChangeNames.NEW_CONNECTION_MULTILINE_PERSONAL_CONTRIBUTION_CAN_DO_WALL_BUSHING,
            ChangeNames.NEW_CONNECTION_MULTILINE_PERSONAL_CONTRIBUTION_MUST_DO_WALL_BUSHING,
            ChangeNames.NEW_CONNECTION_MULTILINE_PERSONAL_CONTRIBUTION_MUST_INSTALL_CASING_PIPE,
            ChangeNames.NEW_CONNECTION_PERSONAL_CONTRIBUTION_CAN_DO_WALL_BUSHING,
            ChangeNames.NEW_CONNECTION_MULTILINE_PERSONAL_CONTRIBUTION_MUST_INFUSE_PROVIDED_INSTALLATION_FRAME,
            ChangeNames.NEW_CONNECTION_MULTILINE_PERSONAL_CONTRIBUTION_MUST_INFUSE_OWN_INSTALLATION_FRAME,
            ChangeNames.NEW_CONNECTION_PERSONAL_CONTRIBUTION_CAN_DO_BASE_PLATE_BUSHING,
            ChangeNames.NEW_CONNECTION_PERSONAL_CONTRIBUTION_MUST_DO_BASE_PLATE_BUSHING,
        ],
        RenderComponent: (props: RenderComponentProps) => {
            const classes = useStyles();
            return (
                <Fragment>
                    <h2 className={classes.h2} style={{ marginLeft: 20 }}>
                        Hauseinführung
                    </h2>
                    <MultilineConnectionTeaser />
                    {props.configuration.error ? (
                        <div className={classes.error}>{props.configuration.error}</div>
                    ) : (
                        <div />
                    )}
                    <div style={{ paddingLeft: 20, paddingRight: 20 }}>
                        <MultiChangeRenderer
                            changes={[
                                ChangeNames.NEW_CONNECTION_MULTILINE_DESIRES,
                                ChangeNames.NEW_CONNECTION_MULTILINE_BUILDING_INTAKE_SPECIFICATION,
                                ChangeNames.NEW_CONNECTION_MULTILINE_CONTACT_ALLOWED,
                                ChangeNames.NEW_CONNECTION_MULTILINE_VARIANTS,
                                ChangeNames.NEW_CONNECTION_MULTILINE_CELLAR_OUTER_WALL,
                                ChangeNames.NEW_CONNECTION_MULTILINE_CELLAR_OUTER_WALL_BUSHING,
                                ChangeNames.NEW_CONNECTION_MULTILINE_CELLAR_CASING_PIPE_DELIVERED_BY_DISTRIBUTION_SYSTEM_OPERATOR,
                                ChangeNames.NEW_CONNECTION_MULTILINE_BASE_PLATE_HEIGHT,
                                ChangeNames.NEW_CONNECTION_MULTILINE_INSTALLATION_FRAME_INSTALLED_BY_DISTRIBUTION_SYSTEM_OPERATOR,
                                ChangeNames.NEW_CONNECTION_MULTILINE_CELLAR_INSTALLATION_FRAME_INSTALLED_BY_DISTRIBUTION_SYSTEM_OPERATOR,
                            ]}
                            configuration={props.configuration}
                            scrollToChangeName={props.options?.scrollToChangeName}
                        />
                        <ContributionsSectionRenderer
                            changes={[
                                ChangeNames.NEW_CONNECTION_MULTILINE_PERSONAL_CONTRIBUTION_CAN_DO_UNDERGROUND_WORK,
                                ChangeNames.NEW_CONNECTION_MULTILINE_PERSONAL_CONTRIBUTION_MUST_DO_UNDERGROUND_WORK,
                                ChangeNames.NEW_CONNECTION_PERSONAL_CONTRIBUTION_CAN_DO_UNDERGROUND_WORK,
                                ChangeNames.NEW_CONNECTION_MULTILINE_PERSONAL_CONTRIBUTION_CAN_DO_WALL_BUSHING,
                                ChangeNames.NEW_CONNECTION_MULTILINE_PERSONAL_CONTRIBUTION_MUST_DO_WALL_BUSHING,
                                ChangeNames.NEW_CONNECTION_MULTILINE_PERSONAL_CONTRIBUTION_MUST_INSTALL_CASING_PIPE,
                                ChangeNames.NEW_CONNECTION_PERSONAL_CONTRIBUTION_CAN_DO_WALL_BUSHING,
                                ChangeNames.NEW_CONNECTION_PERSONAL_CONTRIBUTION_CAN_DO_BASE_PLATE_BUSHING,
                                ChangeNames.NEW_CONNECTION_PERSONAL_CONTRIBUTION_MUST_DO_BASE_PLATE_BUSHING,
                                ChangeNames.NEW_CONNECTION_MULTILINE_PERSONAL_CONTRIBUTION_MUST_INFUSE_PROVIDED_INSTALLATION_FRAME,
                                ChangeNames.NEW_CONNECTION_MULTILINE_PERSONAL_CONTRIBUTION_MUST_INFUSE_OWN_INSTALLATION_FRAME,
                            ]}
                            configuration={props.configuration}
                            scrollToChangeName={props.options?.scrollToChangeName}
                        />
                    </div>
                </Fragment>
            );
        },
    },
    {
        id: "documents",
        name: "Dokumente",
        changes: [],
        RenderComponent: (props: RenderComponentProps) => {
            const classes = useStyles();
            // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
            const mapDocs = (docEntries: [string, DocumentState][]) =>
                docEntries
                    .sort(([, doc1], [, doc2]) => {
                        if (!doc1.relevant || !doc2.relevant) return 0;

                        return doc1.order - doc2.order;
                    })
                    .map(([classification, doc]) => (
                        <DocumentUpload
                            classification={classification}
                            key={classification}
                            document={doc}
                        />
                    ));
            const requiredDocs = Object.entries(props.configuration.documents).filter(
                ([, docState]) => docState.relevant && !docState.optional,
            );
            const optionalDocs = Object.entries(props.configuration.documents).filter(
                ([, docState]) => docState.relevant && docState.optional,
            );
            return (
                <Fragment>
                    {props.configuration.error ? (
                        <div className={classes.error}>{props.configuration.error}</div>
                    ) : (
                        <div />
                    )}
                    {requiredDocs.length ? (
                        <Fragment>
                            <h2 className={classes.h2} style={{ marginLeft: 20 }}>
                                Dokumente
                            </h2>
                            <p className={classes.p} style={{ marginLeft: 20 }}>
                                Laden Sie bitte hier die erforderlichen Dokumente hoch.
                            </p>
                            <div style={{ paddingLeft: 20, paddingRight: 20 }}>
                                {mapDocs(requiredDocs)}
                            </div>
                        </Fragment>
                    ) : (
                        <Fragment />
                    )}
                    {optionalDocs.length ? (
                        <Fragment>
                            <h2 className={classes.h2} style={{ marginLeft: 20 }}>
                                Optionale Dokumente
                            </h2>
                            <div style={{ paddingLeft: 20, paddingRight: 20 }}>
                                {mapDocs(optionalDocs)}
                            </div>
                        </Fragment>
                    ) : (
                        <Fragment />
                    )}
                </Fragment>
            );
        },
    },
    {
        id: "addresses",
        name: "Adressen",
        changes: [
            ChangeNames.INVOICE_RECIPIENT,
            ChangeNames.PROPERTY_OWNER,
            ChangeNames.APPLICANT,
            ChangeNames.SYSTEM_CONSTRUCTOR,
        ],
    },
    {
        id: "dates",
        name: "Termine",
        changes: [ChangeNames.REMOVAL_DATE, ChangeNames.REMOVAL_RECONSTRUCTION_DATE],
    },
    {
        id: summaryScreenName,
        name: "Zusammenfassung",
        changes: [
            ChangeNames.ADDITIONAL_NOTES,
            ChangeNames.DATA_PROTECTION_AGREEMENT,
            ChangeNames.TERMS_OF_USE_AGREEMENT,
            ChangeNames.ADJUSTABLE_LOAD_AGREEMENT,
        ],
        RenderComponent: (props: RenderComponentProps) => {
            const classes = useStyles();
            return (
                <Fragment>
                    <h2 className={classes.h2} style={{ marginLeft: 20 }}>
                        Zusammenfassung
                    </h2>
                    <TopAreaText text="Hier erhalten Sie die Zusammenfassung Ihrer Beantragung. Bitte prüfen Sie Ihre Angaben." />
                    {props.configuration.error ? (
                        <div className={classes.error}>{props.configuration.error}</div>
                    ) : (
                        <div />
                    )}
                    <div style={{ paddingLeft: 20, paddingRight: 20 }}>
                        <Summary configuration={props.configuration} options={props.options} />
                    </div>
                </Fragment>
            );
        },
    },
];

export const screenIdByChange = (change: ChangeNames): string | undefined => {
    return screens.find((s) => s.changes.includes(change))?.id;
};

export const screenRelevant = (screen: Screen): boolean => {
    if (screen.changes.length < 1) return true;
    return (
        screen.changes
            .map((changeName) => selectChange(changeName)?.relevant)
            .reduce((a, b) => a || b) || false
    );
};

export const nextScreenNum = (screenNum: number): number | undefined => {
    const screensAhead = screens.slice(screenNum + 1);
    if (screensAhead) {
        for (let idx = 0; idx < screensAhead.length; idx++) {
            const screen = screensAhead[idx];
            if (screenRelevant(screen)) {
                return idx + screenNum + 1;
            }
        }
    }
    return undefined;
};

export const prevScreenNum = (screenNum: number): number | undefined => {
    const screensBehind = screens.slice(0, screenNum).reverse();
    if (screensBehind) {
        for (let idx = 0; idx < screensBehind.length; idx++) {
            const screen = screensBehind[idx];
            if (screenRelevant(screen)) {
                return screenNum - idx - 1;
            }
        }
    }
    return undefined;
};

export const screenById = (id: string): Screen => {
    const screen = screens.find((it) => it.id === id);
    if (!screen) throw new Error(`there is no screen with name ${id}`);
    return screen;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const containsErrors = (obj: Record<string, any>): boolean =>
    (Object.keys(obj) as Array<keyof typeof obj>).some((key) => {
        if (key === "error" && obj[key]) {
            return true;
        }
        if (obj[key] && typeof obj[key] === "object") {
            return containsErrors(obj[key]);
        }
        return false;
    });

export const screenHasErrors = (screen: Screen): boolean =>
    screen.changes
        .map((changeName) => selectChange(changeName))
        .filter((change) => change?.relevant)
        .some((change) => (change ? containsErrors(change.state) : false));

export const screenIsComplete = (screen: Screen): boolean =>
    screen.changes
        .map((changeName) => selectChange(changeName))
        .filter((change) => change?.relevant)
        .every((change) => (change ? !containsErrors(change) && !change.default : false));

export default screens;
